import React, { useEffect, useRef } from 'react'

import cloudinaryUrl from 'src/utils/cloudinaryUrl'
import { init as initHeartConfetti } from 'src/fancy/falling-confetti/app2'
import BackLink from '../BackLink'
import { COVER_TRANSFORM } from './upsellCover'

// The shared upsell illustration (widgets/donations/upsell.jpg) is
// itself a peach→pink→lavender radial gradient with the heading text
// painted on top. We mimic that gradient as the wrapper background,
// so during the brief moment before the JPG arrives the panel reads
// "designed background, text-to-come" instead of an empty box or
// spinner. Once the JPG paints it covers the gradient cleanly — the
// colors line up so there's no visible swap. Eyeballed from the source
// asset; only applies to the SHARED default. Partner-uploaded
// illustrations could be anything, so they get a neutral bg-gray-200
// placeholder applied via className instead.
const PLACEHOLDER_GRADIENT =
  'radial-gradient(circle at 50% 45%, #f8d5be 0%, #eab8b8 35%, #c5a9d4 75%, #c0a4d2 100%)'

// Heart-confetti palette for the overlay canvas. Pink + coral from
// the Pledge palette (config/initializers/colors.rb) — warm tones
// that fit the "love / monthly support" framing of the upsell ask,
// and read well against the variety of imagery the photo slot may
// carry. RGB tuples per falling-confetti/app2.js's expected shape.
const CONFETTI_COLORS = [
  [ 229, 105, 173 ], // #e569ad pink
  [ 254, 137, 104 ], // #fe8968 coral
]

// Soft nudge to convert small one-time donations into recurring
// monthly support. Eligibility (one-time + USD-converted amount under
// PWC.data.monthly_upsell_threshold, $100 where nothing in the config
// chain sets one + Organization beneficiary, single-org only + monthly
// frequency available + a non-null `monthlyAmount` proposal) is
// decided by App.jsx, as is the `monthlyAmount` proposal itself
// (see proposeMonthlyUpsell.js). The proposal is normally strictly
// smaller than the one-time; when the one-time is at the partner's
// minimum, the two are equal (per manager-decided UX — we still show
// the panel so the donor sees the monthly nudge). This component
// just renders when the upsell panel is reached. confirm sets the
// new amount + flips to monthly and advances; decline advances
// unchanged.
//
// The cover photo defaults to the shared widgets/donations/upsell.jpg
// illustration, and can be overridden via PWC.data.upsell_image (set by
// partners via the IH form, or by staff via SP RecurringDonations.jsx).
// When the partner image is in play, the matching radial gradient
// placeholder is swapped out for a neutral gray — we can't predict
// what the partner uploaded.
const Upsell = ({
  i18n, donation, monthlyAmount, partnerItem, upsellImage,
  onConfirm, onDecline, onBack,
}) => {
  const canvasRef = useRef(null)

  // Heart confetti runs while the upsell panel is mounted, but ONLY
  // over the shared default illustration — partner art lives on its
  // own visually and the hearts would compete with whatever they
  // uploaded. Panel unmounts children when status drops to null
  // (donor moves to another step), so the cleanup function from init
  // fires automatically and the rAF loop tears down.
  //
  // TODO when SP gains a "master record default image" setting: this
  // suppresses hearts the moment ANY upsell_image is present, which
  // would include a Pledge-designed alternate set as the master
  // default. If we want hearts on Pledge-designed defaults but not on
  // partner art, we'll need a second signal (a `upsell_image_hearts`
  // flag, or sniff the S3 path prefix) — they're indistinguishable
  // from the S3 key alone today.
  useEffect(() => {
    if (!canvasRef.current) return
    return initHeartConfetti(canvasRef.current, { colors: CONFETTI_COLORS })
  }, [])

  // Multi-org synthetic beneficiary (id=_multiple) has no single org
  // name to display — substitute an upsell-specific phrase. Not the
  // global `selected_nonprofits` because the body's "help X plan
  // ahead" construction needs a definite reference; bare-plural
  // "selected nonprofits" reads as ungrammatical there. See
  // upsell.org_multiple comment in widgets.en.yml.
  const isMultiBeneficiary = partnerItem.beneficiary.id == '_multiple'
  // Partner-uploaded image (an S3 key in our Filestack bucket) wins;
  // otherwise the shared default. The shared asset lives under the
  // `shared` Cloudinary mappingFolder, while partner uploads sit under
  // the default mappingFolder Cloudinary has wired to the S3 source.
  const coverImage = upsellImage
    ? cloudinaryUrl(upsellImage, COVER_TRANSFORM)
    : cloudinaryUrl(
        'widgets/donations/upsell.jpg',
        COVER_TRANSFORM,
        { mappingFolder: 'shared' },
      )
  const orgName = isMultiBeneficiary
    ? i18n.t('upsell.org_multiple')
    : partnerItem.beneficiary.name
  // formattedOneTime feeds the "Keep my $X one-time gift" decline
  // button — donor sees what they'd be sticking with. formattedMonthly
  // feeds the confirm button's "Donate $Y/month" CTA — donor sees the
  // proposed monthly amount, which is computed in App.jsx via
  // proposeMonthlyUpsell and is strictly smaller than the one-time.
  const formattedOneTime = i18n.formatCurrency(
    donation.state.amount,
    donation.currency,
  )
  const formattedMonthly = i18n.formatCurrency(
    monthlyAmount,
    donation.currency,
  )

  return (
    <div className="space-y-4">
      {/* Heading moved out of the image and made screen-reader only —
          the image speaks for itself visually, and Panel's aria-label
          + this h2 keep the section recognizable to assistive tech. */}
      <h2 className="sr-only">{i18n.t('upsell.heading')}</h2>

      {/* Cover photo with a heart-confetti canvas overlay. The
          wrapper carries a radial-gradient backdrop that matches the
          underlying JPG, so the panel doesn't flicker as the image
          arrives — the JPG paints over the gradient without a visible
          swap. The canvas sits absolutely on top of the image at the
          same size; init() inside the effect above syncs canvas.
          width/height to its offset dimensions and starts the heart
          rain. pointer-events-none keeps it from intercepting clicks
          on the image area. */}
      <div
        className={`relative z-0 overflow-hidden rounded-lg aspect-2/1 ${upsellImage ? 'bg-gray-200' : ''}`}
        style={upsellImage ? undefined : { background: PLACEHOLDER_GRADIENT }}
      >
        <img
          src={coverImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {!upsellImage &&
          <canvas
            ref={canvasRef}
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
        }
      </div>

      <p>{i18n.t('upsell.body', { org: orgName })}</p>

      {/* Confirm on top (the loud CTA), Decline below as a full-width
          outline-primary (it's a real choice, not navigation), then
          Back as a link-style at the bottom aligned to the start —
          matches the rest of the panels' back-link convention. */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={onConfirm}
          className="btn btn-primary flex w-full items-center justify-center gap-2"
        >
          {i18n.t('upsell.confirm', { amount: formattedMonthly })}
        </button>
        <button
          type="button"
          onClick={onDecline}
          className="btn btn-outline-primary flex w-full items-center justify-center gap-2"
        >
          {i18n.t('upsell.decline', { amount: formattedOneTime })}
        </button>
        <BackLink i18n={i18n} onClick={onBack} />
      </div>
    </div>
  )
}

export default Upsell
